import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { WorkPackageType, Status, Priority } from '@/types';
import {
  getTypes,
  getStatuses,
  getPriorities,
  getStatusGroups,
  type StatusGroups,
} from '@/services/metadata.service';
import { useAuth } from './AuthContext';

interface MetadataContextType {
  types: WorkPackageType[];
  statuses: Status[];
  priorities: Priority[];
  statusGroups: StatusGroups;
  isLoading: boolean;
  error: string | null;
  refreshMetadata: () => Promise<void>;
  getTypeById: (id: number) => WorkPackageType | undefined;
  getStatusById: (id: number) => Status | undefined;
  getPriorityById: (id: number) => Priority | undefined;
}

const defaultStatusGroups: StatusGroups = {
  todo: [],
  inProgress: [],
  completed: [],
  cancelled: [],
};

const MetadataContext = createContext<MetadataContextType | undefined>(undefined);

export function MetadataProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [types, setTypes] = useState<WorkPackageType[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [statusGroups, setStatusGroups] = useState<StatusGroups>(defaultStatusGroups);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMetadata = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const [typesData, statusesData, prioritiesData, statusGroupsData] =
        await Promise.all([
          getTypes(),
          getStatuses(),
          getPriorities(),
          getStatusGroups(),
        ]);

      setTypes(typesData);
      setStatuses(statusesData);
      setPriorities(prioritiesData);
      setStatusGroups(statusGroupsData);
    } catch (err) {
      console.error('Failed to load metadata:', err);
      setError('Eroare la încărcarea metadatelor');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const getTypeById = useCallback(
    (id: number) => types.find((t) => t.id === id),
    [types]
  );

  const getStatusById = useCallback(
    (id: number) => statuses.find((s) => s.id === id),
    [statuses]
  );

  const getPriorityById = useCallback(
    (id: number) => priorities.find((p) => p.id === id),
    [priorities]
  );

  const value: MetadataContextType = {
    types,
    statuses,
    priorities,
    statusGroups,
    isLoading,
    error,
    refreshMetadata: loadMetadata,
    getTypeById,
    getStatusById,
    getPriorityById,
  };

  return (
    <MetadataContext.Provider value={value}>
      {children}
    </MetadataContext.Provider>
  );
}

export function useMetadata(): MetadataContextType {
  const context = useContext(MetadataContext);
  if (context === undefined) {
    throw new Error('useMetadata must be used within a MetadataProvider');
  }
  return context;
}
